import {
  Box,
  BoxProps,
  Flex,
  Input,
  InputProps,
  List,
  ListItem,
  Tag,
  TagCloseButton,
  TagLabel,
  TagRightIcon,
} from '@chakra-ui/react'
import { useCombobox, useMultipleSelection } from 'downshift'
import * as React from 'react'
import { Delete } from '../../../icons'
import { useColor } from '../../../theme'
import { IFormValidationProps } from '../hooks'
import { TComboboxOption } from '../types'

export const MultiCombobox = React.forwardRef<
  HTMLInputElement,
  IFormValidationProps & Omit<BoxProps, 'onSelect'> & Pick<InputProps, 'placeholder'> & {
    defaultOptions?: TComboboxOption[]
    options: TComboboxOption[]
    isDisabled?: boolean
    noResultsMessage: string
    clearAllMessage?: string
    noMoreOptionsPlaceholder: string
    onSelect: (options: TComboboxOption[] | null) => void
  }
>(
  (
    {
      defaultOptions = [],
      isDisabled = false,
      isInvalid,
      Label,
      noResultsMessage,
      clearAllMessage,
      noMoreOptionsPlaceholder,
      options,
      onSelect,
      placeholder,
      ...boxProps
    },
    ref,
  ) => {
    const clearColor = useColor({ nuance: 'purple', palette: 'colors' })
    const disabledTextColor = useColor({ nuance: 0, palette: 'text' })
    const errorColor = useColor({ nuance: 'red', palette: 'colors' })
    const listItemColor = useColor({ nuance: 0, palette: 'secondary' })
    const listItemHoverColor = useColor({ nuance: 2, palette: 'secondary' })
    const mainColor = useColor({ nuance: 0, palette: 'secondary' })
    const mainColorHover = useColor({ nuance: 1, palette: 'secondary' })
    const tagColor = useColor({ nuance: 4, palette: 'secondary' })
    const textColor = useColor({ nuance: 1, palette: 'text' })

    const [inputValue, setInputValue] = React.useState<string>('')
    const [innerOptions, setInnerOptions] = React.useState(options)

    const {
      addSelectedItem,
      getDropdownProps,
      getSelectedItemProps,
      removeSelectedItem,
      reset,
      selectedItems,
      setSelectedItems,
    } = useMultipleSelection<TComboboxOption | undefined>({
      initialSelectedItems: defaultOptions as Array<
        TComboboxOption | undefined
      >,
    })

    const getFilteredOptions = React.useCallback(
      () =>
        innerOptions.filter(
          (option) => !selectedItems.find((item) => item?.id === option.id),
        ),
      [innerOptions, selectedItems],
    )

    const {
      closeMenu,
      getComboboxProps,
      getInputProps,
      getItemProps,
      getLabelProps,
      getMenuProps,
      highlightedIndex,
      isOpen,
      openMenu,
    } = useCombobox({
      defaultIsOpen: false,
      inputValue,
      itemToString: (item) => item?.label ?? '',
      items: getFilteredOptions(),
      onInputValueChange: ({ inputValue: inputValueOnValueChange }) => {
        setInputValue(inputValueOnValueChange ?? '')

        setInnerOptions(
          options.filter((item) =>
            item.label.toLowerCase().includes((inputValueOnValueChange ?? '').toLowerCase()),
          ),
        )
      },
      onSelectedItemChange: ({ selectedItem: selectedOption }) => {
        if (selectedOption) {
          addSelectedItem(selectedOption)
        }
      },
      selectedItem: null,
      stateReducer: (_, { changes, type }) => {
        // Keep the menu open after selection.
        if (
          type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
          type === useCombobox.stateChangeTypes.ItemClick
        ) {
          return {
            ...changes,
            isOpen: true,
          }
        }

        return changes
      },
    })

    const handleOnFocus = React.useCallback(() => {
      // Make it behave like Google search.
      if (!isOpen) {
        openMenu()
      }
    }, [isOpen, openMenu])

    const hasNoMoreOptions = React.useMemo(
      () => selectedItems.length === options.length,
      [options, selectedItems],
    )

    const handleRemoveSelectedItem = React.useCallback(
      (selectedItem?: TComboboxOption) =>
        (event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()

          removeSelectedItem(selectedItem)
        },
      [removeSelectedItem],
    )

    const handleClearSelection = React.useCallback(() => {
      reset()

      closeMenu()

      onSelect([])
    }, [closeMenu, onSelect, reset])

    // Ensure default option is set correctly after updates (controlled).
    React.useEffect(() => {
      if (defaultOptions.length > 0) {
        setSelectedItems(defaultOptions as TComboboxOption[])
      }
    }, [defaultOptions, setSelectedItems])

    React.useEffect(() => {
      setInnerOptions(options)
    }, [options])

    React.useEffect(() => {
      onSelect(selectedItems as TComboboxOption[])
    }, [onSelect, selectedItems])

    return (
      <Box
        data-testid="multi-combobox"
        position="relative"
        ref={ref}
        {...boxProps}
        {...getComboboxProps()}
        {...(isDisabled ? { cursor: 'not-allowed' } : {})}>
        {Label && <Label {...getLabelProps()} />}
        <Flex
          _focusWithin={{
            boxShadow: 'outline',
            outline: 'none',
          }}
          _hover={{ backgroundColor: mainColorHover }}
          backgroundColor={mainColor}
          borderRadius="md"
          direction="row"
          flexWrap="wrap"
          padding="0.6rem"
          {...(isDisabled ? { opacity: 0.4, pointerEvents: 'none' } : {})}
          {...(isInvalid
            ? { borderColor: errorColor, borderWidth: '1px' }
            : {})}>
          {selectedItems.length > 1 && (
            <Tag
              backgroundColor={clearColor}
              cursor="pointer"
              data-testid="multi-combobox-clear"
              margin="0.3rem"
              onClick={handleClearSelection}
              tabIndex={0}
              variant="secondary">
              {clearAllMessage && <TagLabel>{clearAllMessage}</TagLabel>}
              <TagRightIcon
                as={Delete}
                marginInlineStart={clearAllMessage ? 'inherit' : 0}
              />
            </Tag>
          )}
          {selectedItems.map((selectedItem, index) => (
            <Tag
              backgroundColor={tagColor}
              data-testid={`multi-combobox-tag-${selectedItem?.label}`}
              key={selectedItem?.id ?? index}
              margin="0.3rem"
              variant="solid"
              {...getSelectedItemProps({
                index,
                selectedItem,
              })}>
              <TagLabel>{selectedItem?.label}</TagLabel>
              {!isDisabled && (
                <TagCloseButton
                  data-testid={`multi-combobox-remove-tag-${selectedItem?.label}`}
                  onClick={handleRemoveSelectedItem(selectedItem)}
                />
              )}
            </Tag>
          ))}
          <Input
            autoComplete="false"
            color={isDisabled ? disabledTextColor : textColor}
            data-testid="multi-combobox-input"
            flexGrow={1}
            fontWeight="500"
            isInvalid={isInvalid}
            marginLeft="0.3rem"
            placeholder={
              hasNoMoreOptions ? noMoreOptionsPlaceholder : placeholder
            }
            variant="unstyled"
            width="auto"
            {...getInputProps(
              getDropdownProps({
                disabled: hasNoMoreOptions || isDisabled,
                onFocus: handleOnFocus,
              }),
            )}
          />
        </Flex>
        <Box {...getMenuProps()}>
          {isOpen && (
            <List
              backgroundColor={mainColor}
              borderRadius="md"
              data-testid="multi-combobox-list"
              marginTop="0.6rem"
              maxHeight="200px"
              overflowY="auto"
              paddingY="0.3rem"
              position="absolute"
              shadow="md"
              sx={{ width: '100%' }}
              zIndex="dropdown">
              {getFilteredOptions().map((option, index) => (
                <ListItem
                  _hover={{
                    backgroundColor: listItemHoverColor,
                  }}
                  backgroundColor={
                    highlightedIndex === index ? listItemColor : 'transparent'
                  }
                  color={textColor}
                  cursor="pointer"
                  data-testid={`multi-combobox-list-item-${option.label}`}
                  fontSize="md"
                  fontWeight="500"
                  key={option.id}
                  padding="0.3rem"
                  paddingLeft="1rem"
                  {...getItemProps({ index, item: option })}>
                  {option.label}
                </ListItem>
              ))}
              {getFilteredOptions().length === 0 && (
                <ListItem
                  data-testid="multi-combobox-list-no-results"
                  fontSize="md"
                  fontWeight="500"
                  padding="0.3rem"
                  paddingLeft="1rem">
                  {noResultsMessage}
                </ListItem>
              )}
            </List>
          )}
        </Box>
      </Box>
    )
  },
)
